import { useMutation, useQuery } from '@apollo/client';
import {
  HeaderTag,
  Loader,
  SaveCard,
  toast,
  EventEmitter,
  getTranslatedText,
  withErrorBoundary,
  SecureText
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  CREATE_ASSET,
  GET_NON_MONITORED_ASSET_CLASS,
  GET_ENTITY_META
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import ComponentResolver from '@nile/rmm/src/modules/common/components/sub-pages/component-resolver';
import {
  El,
  LayoutFormPage,
  NonMonitoredAssetCreateParent
} from '@nile/rmm/src/modules/it-documentation/components/pages/non-monitored-asset-create/styles';
import {
  Heading,
  Button,
  ButtonGroup,
  TextField,
  Banner
} from '@superops/ui-design-system';
import { cloneDeep, isEmpty, map } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import { withRouter } from 'react-router-dom';
import DynamicField from '@nile/rmm/src/modules/common/components/sub-pages/dynamic-field';
import { CUSTOM_FIELDS } from '@nile/rmm/src/constants';
import { SVGIcon } from '@superops/super-icons';
import { useIntl, FormattedMessage } from 'react-intl';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

const ReactGridLayout = WidthProvider(RGL);

function NonMonitoredAssetCreate(props) {
  const {
    entityName,
    classId,
    formView = false,
    emitChange,
    defaultCustomFields = {}
  } = props.entityName ? props : props.match.params;
  const intl = useIntl();
  const tabContext = useContext(TicketContext);

  const [entityMeta, setEntityMeta] = useState({});

  const [assetClass, setAssetClass] = useState({});

  const [layout, setLayout] = useState([]);

  const [assetName, setAssetName] = useState('');

  const [errors, setErrors] = useState({});

  const [selectedClient, setSelectedClient] = useState(props.client || null);

  const [selectedSite, setSelectedSite] = useState(props.site || null);

  const [loader, setLoader] = useState(false);
  const ITDocsDetailsRef = React.useRef({});
  const [showTotpBanner, setShowTotpBanner] = useState(false);
  const isITProduct = useCheckIsItProduct();

  useQuery(GET_ENTITY_META, {
    client: RMMClient,
    skip: !entityName,
    fetchPolicy: 'network-only',
    variables: {
      entityName
    },
    onCompleted: (res) => {
      setEntityMeta(res?.getEntityMeta || null);
    }
  });

  useQuery(GET_NON_MONITORED_ASSET_CLASS, {
    client: RMMClient,
    skip: !classId,
    fetchPolicy: 'network-only',
    variables: {
      classId
    },
    onCompleted: (res) => {
      setAssetClass(res?.getNonMonitoredAssetClass);
      if (
        res?.getNonMonitoredAssetClass?.customFields?.some(
          (obj) => obj?.fieldType === CUSTOM_FIELDS.totp
        )
      ) {
        setShowTotpBanner(true);
      }
      const layout = JSON.parse(
        res?.getNonMonitoredAssetClass.formMeta
      ).layout.map((el) => {
        return {
          ...el,
          isDraggable: false,
          isResizable: false
        };
      });
      setLayout(layout);
    }
  });

  const getCustomFields = () => {
    const filteredFields = entityMeta.attributes.filter(
      (attribute) => attribute.isCustom === true
    );
    const x = {};
    map(filteredFields, 'name').map((field) => {
      x[field] = null;
    });
    setCustomFields({
      customFields: x
    });
  };

  useEffect(() => {
    if (entityMeta?.attributes && !ITDocsDetailsRef.current) {
      getCustomFields();
    }
  }, [entityMeta]);

  function useAsyncReference(value, isProp = false) {
    const ref = useRef(value);
    const [, forceRender] = useState(false);
    function updateState(newState) {
      if (!Object.is(ref.current, newState)) {
        ref.current = newState;
        forceRender((s) => !s);
      }
    }
    if (isProp) {
      ref.current = value;
      return ref;
    }
    return [ref, updateState];
  }

  const [customFields, setCustomFields] = useAsyncReference({
    customFields: {}
  });

  const getDynamicFieldConfig = (field) => {
    switch (field.name) {
      case 'site':
        return {
          dependentFieldId: selectedClient?.accountId || null,
          dependentFieldName: 'client'
        };
      default:
        return {};
    }
  };

  const getFieldValue = (fieldName) => {
    if (formView && emitChange) {
      return defaultCustomFields[fieldName];
    }
    return customFields.current.customFields[fieldName];
  };

  const renderRespectiveLayout = (fieldObj) => {
    switch (fieldObj.field.type) {
      case 'custom':
        const i = assetClass.customFields.findIndex((field) => {
          return fieldObj.field.fieldProps.label === field.label;
        });
        if (i < 0) {
          return <></>;
        }
        return (
          <div
            className={`flex fcol ${
              assetClass.customFields[i].fieldType === CUSTOM_FIELDS.RICH_TEXT
                ? 'full-height'
                : ''
            }`}
          >
            <div
              className={`flex-row ${
                assetClass.customFields[i].fieldType === CUSTOM_FIELDS.RICH_TEXT
                  ? 'full-height'
                  : ''
              }`}
            >
              {assetClass.customFields[i]?.fieldType ===
              CUSTOM_FIELDS.secureText ? (
                <SecureText
                  module={entityName}
                  defaultValue={getFieldValue(
                    assetClass.customFields[i]?.columnName
                  )}
                  field={assetClass.customFields[i]}
                  value={getFieldValue(assetClass.customFields[i]?.columnName)}
                  updateSelection={(val) => {
                    handleChange(assetClass.customFields[i]?.columnName, val);
                  }}
                  editable
                />
              ) : (
                <ComponentResolver
                  attribute={assetClass.customFields[i]}
                  handleChange={(value) => {
                    handleChange(assetClass.customFields[i].columnName, value);
                  }}
                  value={getFieldValue(assetClass.customFields[i].columnName)}
                />
              )}
            </div>
          </div>
        );
      case 'assetDefault':
        const filteredField = assetClass.assetDefaultFields.find((field) => {
          return fieldObj.field.fieldProps.id === field.id;
        });

        if (
          filteredField.hasOwnProperty('isDynamic') &&
          filteredField.isDynamic === true
        ) {
          return (
            <div className="flex fcol">
              <div className="flex-row flex-align-center full-width">
                <DynamicField
                  isClearable
                  field={filteredField}
                  value={getDynamicQueryDefaultValue(filteredField)}
                  config={
                    !isITProduct ? getDynamicFieldConfig(filteredField) : {}
                  }
                  handleChange={(value) => {
                    handleChange(filteredField.name, value);
                  }}
                />
                {/* <ComponentResolver
                  attribute={filteredField}
                  handleChange={(value) => {
                    handleChange(filteredField.name, value);
                  }}
                  value={''}
                ></ComponentResolver> */}
              </div>
            </div>
          );
        }
        return (
          <div className="flex fcol">
            <div className="flex-row flex-align-center">
              <ComponentResolver
                attribute={filteredField}
                handleChange={(value) => {
                  handleChange(filteredField.name, value);
                }}
                value={getFieldValue(filteredField.name)}
              />
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="ml-5">
            <Heading element="h3" weight="Bold">
              {fieldObj.field.fieldProps.label}
            </Heading>
          </div>
        );
      case 'sectionEnd':
        return <div className="divider" />;
      default:
        break;
    }
  };

  const back = () => {
    props.toggleITDocPopup();
  };

  const [createAsset] = useMutation(CREATE_ASSET, {
    client: RMMClient,
    onCompleted(res) {
      if (res) {
        toast('success', 'itdocs.create.success.message');
        setLoader(false);
        if (props.toggleITDocPopup) {
          props.toggleITDocPopup(true);
        }
      }
    },
    onError() {
      setLoader(false);
      // toast('error', 'Something went wrong');
    }
  });

  const createNonMonitoredAsset = (data) => {
    setLoader(true);
    createAsset({
      input: { input: data },
      variables: { input: data }
    });
  };

  const handleSave = () => {
    if (!isEmpty(assetName)) {
      let obj = {
        assetClass: {
          classId
        },
        name: assetName
      };
      if (selectedClient) {
        obj['client'] = isEmpty(selectedClient?.accountId)
          ? null
          : {
              accountId: selectedClient.accountId
            };

        obj['site'] = null;
      }
      if (selectedSite) {
        obj['site'] = isEmpty(selectedSite?.id)
          ? null
          : {
              id: selectedSite?.id
            };
      }
      createNonMonitoredAsset({
        ...obj,
        ...customFields.current
      });
    } else {
      setErrors({ name: "Asset name can't be empty" });
    }
  };

  const handleChange = (fieldName, value) => {
    if (typeof value === 'object' && value.isDynamic === true) {
      switch (fieldName) {
        case 'client':
          setSelectedClient(value);
          setSelectedSite(null);
          break;
        case 'site':
          setSelectedSite(value);
          break;
        default:
          break;
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (emitChange) {
        emitChange({
          [fieldName]: value
        });
      } else {
        const clonedCustomFields = cloneDeep(customFields.current);
        clonedCustomFields.customFields[fieldName] = value;
        setCustomFields(clonedCustomFields);
      }
    }
  };

  const getDynamicQueryDefaultValue = (field) => {
    switch (field.name) {
      case 'client':
        return selectedClient?.name || null;
      case 'site':
        return selectedSite?.name || null;
      default:
        break;
    }
  };

  const handleAssetNameChange = (name) => {
    setAssetName(name);
  };

  ITDocsDetailsRef.current = {
    assetClass,
    assetName,
    selectedClient,
    selectedSite,
    customFields
  };
  const updateTabState = (eventData) => {
    tabContext.updateTabState(
      {
        ITDocDetails: ITDocsDetailsRef.current
      },
      eventData
    );
  };
  useEffect(() => {
    EventEmitter.subscribe('tabChange', updateTabState);
    return function cleanup() {
      EventEmitter.unsubscribe('tabChange');
    };
  }, []);
  useEffect(() => {
    const preservedState = tabContext?.activeTab?.state;

    if (preservedState?.ITDocDetails) {
      const {
        assetName: preservedAssetName,
        assetClass: preservedAssetClass,
        selectedClient: preservedClient,
        selectedSite: preservedSite,
        customFields: preservedCustomFields
      } = preservedState?.ITDocDetails;
      setAssetName(preservedAssetName);
      setAssetClass(preservedAssetClass);
      setSelectedClient(preservedClient);
      setSelectedSite(preservedSite);
      setCustomFields(preservedCustomFields.current);
    }
  }, []);

  const showAssetClass =
    !isEmpty(assetClass) && !isEmpty(customFields) && layout;

  return (
    <div className="bg-white br10">
      <NonMonitoredAssetCreateParent>
        {showAssetClass ? (
          <div>
            {!formView && (
              <div className="divider flex actr fx-sb p2040">
                <Heading element="h2">Create {assetClass?.name} </Heading>
                <div>
                  <ButtonGroup spacing="loose">
                    <Button onClick={() => back()} plainSecondary>
                      {getTranslatedText(intl, `common.cancel`)}
                    </Button>
                    <Button
                      onClick={() => handleSave()}
                      icon={<SVGIcon name="Save" />}
                      primary
                      loading={loader}
                    >
                      {getTranslatedText(intl, `common.save`)}
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            )}
            {!formView ? (
              <div className="inner-content p2040">
                <div className="mb-20">
                  <TextField
                    label="Name"
                    value={assetName}
                    onChange={handleAssetNameChange}
                    customClassName="ml-0"
                    error={errors?.name}
                    placeholder={getTranslatedText(intl, 'common.enter.name')}
                  />
                </div>
                <LayoutFormPage className="custom-form-layout x-hidden">
                  <ReactGridLayout
                    className="layout"
                    layout={layout}
                    rowHeight={40}
                    cols={2}
                    margin={[10, 10]}
                    preventCollision={false}
                    verticalCompact
                  >
                    {layout.map((obj) => {
                      return (
                        <div className="scroll" key={obj.i}>
                          {renderRespectiveLayout(obj)}
                        </div>
                      );
                    })}
                  </ReactGridLayout>
                  {showTotpBanner && (
                    <Banner status="info">
                      <FormattedMessage id="admin.itDoc.totp.banner" />
                    </Banner>
                  )}
                </LayoutFormPage>
              </div>
            ) : (
              <LayoutFormPage className="custom-form-layout">
                <ReactGridLayout
                  className="layout"
                  layout={layout}
                  rowHeight={40}
                  cols={2}
                  margin={[10, 10]}
                  preventCollision={false}
                  verticalCompact
                >
                  {layout.map((obj) => {
                    return (
                      <div className="scroll" key={obj.i}>
                        {renderRespectiveLayout(obj)}
                      </div>
                    );
                  })}
                </ReactGridLayout>
              </LayoutFormPage>
            )}
          </div>
        ) : (
          <Loader />
        )}
      </NonMonitoredAssetCreateParent>
    </div>
  );
}

export default withErrorBoundary(withRouter(NonMonitoredAssetCreate));
